
<script setup>
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { User, useRoute } from "#imports";
import { useModyApi } from "~/middelware/ModyApi";
import { useUserDataStorage } from "~/store/userdata";
const router = useRouter()

useSeoMeta({
  title: "App Login",
  ogTitle: "App Login"
})

const route = useRoute();

const email = ref("");
const password = ref("");
const showErrorModal = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref("Bei der Anmeldung ist etwas schief gelaufen.");

const userDataStore = useUserDataStorage();
const { user } = storeToRefs(userDataStore)

const callbackUrl = computed(() => {
  const raw = route.query.callbackUrl || route.query.callback || route.query.redirect_uri || route.query.returnTo;
  return typeof raw === "string" ? raw : "";
});

const callbackState = computed(() => {
  const raw = route.query.state;
  return typeof raw === "string" ? raw : "";
});

const isAppLogin = computed(() => callbackUrl.value.length > 0);

function isAllowedCallback(target) {
  if (!target) return false;
  return target.startsWith("mody://") || target.startsWith("https://");
}

function buildCallbackUrl(status) {
  if (!isAllowedCallback(callbackUrl.value)) {
    return "";
  }

  try {
    const url = new URL(callbackUrl.value);
    url.searchParams.set("status", status);

    if (callbackState.value) {
      url.searchParams.set("state", callbackState.value);
    }

    return url.toString();
  } catch {
    return "";
  }
}

function redirectToApp(status) {
  const target = buildCallbackUrl(status);
  if (!target || !import.meta.client) {
    return;
  }
  window.location.href = target;
}

async function login() {
  if (isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    user.value = await useModyApi().login(email.value, password.value);
    user.value.role = await useModyApi().getUserRole();
    password.value = "";

    if (isAppLogin.value) {
      redirectToApp(user.value.token);
      return;
    }

    await router.replace('/admin')
  }
  catch (e) {
    password.value = "";
    errorMessage.value = isAppLogin.value
      ? "Anmeldung fehlgeschlagen. Bitte prüfe deine Daten und versuche es erneut." + e
      : "Bei der Anmeldung ist etwas schief gelaufen.";
    showErrorModal.value = true;
  }
  finally {
    isSubmitting.value = false;
  }
}

async function logout() {
  user.value = new User();

  if (isAppLogin.value) {
    redirectToApp("logout");
  }
}
</script>

<template>
  <div>
    <div v-if="!userDataStore.loggedIn">
      <form @submit.prevent="login" class="login-form">
        <NuxtImg src="/rgb_negativ.png" alt="Mody Logo" class="w-[200px] m-auto mb-4"/>
        <h1 class="login-title">Mody Login</h1>
        <p v-if="isAppLogin" class="login-subtitle">
          Melde dich an, um zur App zurückzukehren.
        </p>

        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-user"></i>
          </InputGroupAddon>
          <InputText id="email" placeholder="E-Mail" autocomplete="username" v-model="email" />
        </InputGroup>

        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-lock"></i>
          </InputGroupAddon>
          <InputText id="password" type="password" placeholder="Passwort" autocomplete="current-password" v-model="password" />
        </InputGroup>

        <Button type="submit" :loading="isSubmitting">Login</Button>
      </form>
    </div>

    <div v-else>
      <Card class="user-container">
        <template #header>
          <i class="pi pi-user"></i>
        </template>
        <template #title>
          <div>Hallo {{ user?.username }}!</div>
        </template>
        <template #content>
          <p v-if="isAppLogin" class="mb-3">
            Du bist angemeldet. Du kannst jetzt zur App zurückkehren.
          </p>
          <InputText v-model="user.email" disabled class="mb-3"/>
          <template v-if="!isAppLogin">
            <NuxtLink to="/myEvents">
              <Button outlined class="w-100">Meine Events</Button>
            </NuxtLink>
          </template>
          <template v-else>
            <Button class="w-100" @click="redirectToApp(user?.token ?? '')">Zur App zurück</Button>
          </template>
        </template>
        <template #footer>
          <Button @click="logout">Logout</Button>
        </template>
      </Card>
    </div>

    <Dialog v-model:visible="showErrorModal" modal header="Fehler" @hide="password = ''">
      <p>{{ errorMessage }}</p>
      <div v-if="isAppLogin" class="mt-3 text-right">
        <Button label="Zurück zur App" outlined @click="redirectToApp('error')" />
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
.login-form {
  padding: 20px;
  margin-top: 50%;
  margin-bottom: 50%;
  text-align: center;

  @include desktop() {
    width: calc($desktop-content-width - 100px);
    margin: auto auto 100px auto;
  }

  .login-title {
    margin-bottom: 0.5rem;
  }

  .login-subtitle {
    margin-bottom: 1.5rem;
    opacity: 0.8;
  }

  :deep(.p-image) {
    display: block;
    margin-bottom: 20px;
  }

  .p-inputgroup {
    margin-bottom: 20px;
  }

  .p-button {
    width: 100%;
    display: block;
  }
}

.user-container {
  margin: 20px;
  background: none;

  @include desktop() {
    width: calc($desktop-content-width - 100px);
    margin: auto auto 100px auto;
  }

  :deep(.p-card-header) {
    text-align: center;
    padding: 1.5rem;

    .pi {
      background: $myOrange;
      padding: 30px;
      border-radius: 50%;
      color: $myGrey;
      font-size: 3rem;
    }
  }

  :deep(.p-card-content) {
    .p-inputtext {
      width: 100%;
    }

    .p-button {
      width: 100%;
      display: block;
    }
  }

  :deep(.p-card-footer) {
    .p-button {
      width: 100%;
      display: block;
    }
  }
}
</style>
